import type { Money } from "@/lib/api/types";
import { money } from "@/lib/format/money";
import { cn } from "@/lib/utils";

/**
 * Money on the counter (DESIGN.md §5): Literata, tabular, right-aligned by
 * default. A sans never sets an amount, and the figure is always the server's
 * `display` — this component never computes one.
 *
 *   line     20 px, the amount on an order row
 *   total    32 px, the bill total
 *   due      48 px, the amount due on tender and the customer display
 *
 * `saving` is the money-words rule in code (DESIGN.md §3): a customer saving is
 * not money leaving, so it is honey text with a leading minus, never red.
 * Ledger Ink printed every deduction in debit ink; alert is failure only.
 */
export type AmountScale = "line" | "total" | "due" | "inherit";

const SCALE: Record<AmountScale, string> = {
  line: "text-xl",
  total: "text-3xl",
  due: "text-4xl",
  inherit: "",
};

export function Amount({
  value,
  scale = "inherit",
  saving = false,
  strong = false,
  className,
}: Readonly<{
  value: Money | undefined | null;
  scale?: AmountScale;
  saving?: boolean;
  strong?: boolean;
  className?: string;
}>) {
  return (
    <span
      className={cn(
        "font-ledger tabular-nums",
        SCALE[scale],
        saving ? "text-text-accent" : "text-text-strong",
        strong && "font-semibold",
        className,
      )}
    >
      {saving ? "−" : ""}
      {money(value)}
    </span>
  );
}
